import { ApplicationContext } from '@upside/spa-shared-context';

import { globalHeaderAppName, paths } from 'shell/const';
import {
  addErrorHandler,
  getAppStatus,
  registerApplication,
  LifeCycles,
  LOAD_ERROR,
  SKIP_BECAUSE_BROKEN,
} from 'single-spa';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const globalHeaderError = require('../global-header-error.html');

const GLOBAL_HEADER_SELECTOR = '#global-header-container';
const FALLBACK_SELECTOR = '.globalHeaderError';

/**
 * List of paths where the app header should not be shown
 */
const PATHS_WITH_GLOBAL_HEADER = [
  `/${paths.ACCOUNT}`,
  `/${paths.ASSET_SETTLEMENT}`,
  `/${paths.DATA_MANAGEMENT}`,
  `/${paths.EVENTS_SUMMARY_UI}`,
  `/${paths.FLEX_TRADE_MANAGER}`,
  `/${paths.GITBOOK}`,
  `/${paths.ONBOARDING}`,
  `/${paths.REPORTING}`,
  `/${paths.SCHEDULES}`,
  `/${paths.PORTFOLIO_OPTIMIZER}`,
  `/${paths.USER_MANAGEMENT}`,
];

/**
 * Whether the global header should be shown for the current authentication state and path.
 */
const shouldShowGlobalHeader = (
  context: ApplicationContext,
  pathname: string,
): boolean =>
  context.authentication.loggedIn &&
  PATHS_WITH_GLOBAL_HEADER.some((path) => pathname.startsWith(path));

/**
 * Registers global header app if the local storage feature flag is set
 */
export const registerGlobalHeader = (context: ApplicationContext): void => {
  if (!getAppStatus(globalHeaderAppName)) {
    registerApplication({
      activeWhen: ({ pathname }) => shouldShowGlobalHeader(context, pathname),
      app: () => System.import(globalHeaderAppName) as Promise<LifeCycles>,
      name: globalHeaderAppName,
      customProps: {
        context,
        domElementGetter: () => document.querySelector(GLOBAL_HEADER_SELECTOR),
      },
    });

    // The global header is registered programmatically (not via the single-spa-layout
    // template), so its error template can't be wired up declaratively. When the app is
    // broken, render the fallback into its container - but only on routes where the global
    // header should be shown, and remove it again otherwise. This is re-evaluated on every
    // single-spa routing event so a stale fallback can't leak onto a different view.
    const renderGlobalHeaderFallback = (): void => {
      const container = document.querySelector(GLOBAL_HEADER_SELECTOR);

      if (!container) {
        return;
      }

      const status = getAppStatus(globalHeaderAppName);
      const isBroken = status === LOAD_ERROR || status === SKIP_BECAUSE_BROKEN;
      const shouldShow =
        isBroken && shouldShowGlobalHeader(context, window.location.pathname);
      const hasFallback = Boolean(container.querySelector(FALLBACK_SELECTOR));

      if (shouldShow && !hasFallback) {
        container.innerHTML = globalHeaderError;
        // The container is hidden by default (inline display:none) and only revealed via
        // `.has-global-header`, which the global header app adds when it mounts. Since the
        // app failed, add the class ourselves so the fallback is visible.
        document.body.classList.add('has-global-header');
      } else if (!shouldShow && hasFallback) {
        container.innerHTML = '';
        document.body.classList.remove('has-global-header');
      }
    };

    addErrorHandler((error) => {
      if (error.appOrParcelName === globalHeaderAppName) {
        renderGlobalHeaderFallback();
      }
    });

    window.addEventListener(
      'single-spa:routing-event',
      renderGlobalHeaderFallback,
    );
  }
};
